import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './AddTransaction.module.scss';
import WheelDateTimePicker from '../WheelDateTimePicker/WheelDateTimePicker';
import UniversalModal from '../UniversalModal/UniversalModal';
import { categoryIcons } from '../../assets/icons/categoryIcons';
import { triggerHaptic, hapticImpact } from '../../utils/haptics';
import SuccessOverlay from '../SuccessOverlay/SuccessOverlay';
import { AnimatePresence } from 'framer-motion';

function pad2(n) {
  return String(n).padStart(2, '0');
}

function toLocalDateInputValue(d = new Date()) {
  const year = d.getFullYear();
  const month = pad2(d.getMonth() + 1);
  const day = pad2(d.getDate());
  return `${year}-${month}-${day}`;
}

function formatWithSpaces(digits) {
  const s = String(digits || '').replace(/\D/g, '');
  if (!s) return '';
  return s.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

export default function AddTransaction({ onCreated }) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 600
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const isRememberEnabled =
    localStorage.getItem('remember_last_category') === 'true';

  const [type, setType] = useState('expense');

  const [categoryId, setCategoryId] = useState(() => {
    if (isRememberEnabled) {
      return localStorage.getItem('last_expense_category') || '';
    }
    return '';
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');

    if (action === 'add_expense' || action === 'add_income') {
      setIsModalOpen(true);

      const targetType = action === 'add_expense' ? 'expense' : 'income';
      setType(targetType);

      const isRememberEnabled =
        localStorage.getItem('remember_last_category') === 'true';
      if (isRememberEnabled) {
        const savedCategory = localStorage.getItem(
          `last_${targetType}_category`
        );
        setCategoryId(savedCategory || '');
      }

      const newUrl =
        window.location.protocol +
        '//' +
        window.location.host +
        window.location.pathname;
      window.history.replaceState({ path: newUrl }, '', newUrl);
    }
  }, []);

  const [date, setDate] = useState(() => toLocalDateInputValue());
  const [time, setTime] = useState(() => {
    const d = new Date();
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  });

  const [description, setDescription] = useState('');
  const [cats, setCats] = useState([]);
  const [submitState, setSubmitState] = useState('idle');

  const [touchedSubmit, setTouchedSubmit] = useState(false);
  const [amountError, setAmountError] = useState('');
  const [categoryError, setCategoryError] = useState('');

  const amountRef = useRef(null);
  const descRef = useRef(null);
  const overlayRef = useRef(null);

  const [amountRaw, setAmountRaw] = useState('');

  const amountView = useMemo(() => {
    return amountRaw.replace(/\d+/g, (m) => formatWithSpaces(m));
  }, [amountRaw]);

  const amtNum = useMemo(() => {
    try {
      if (!amountRaw || /[+\-*/]$/.test(amountRaw)) return 0;
      const res = new Function(`return ${amountRaw}`)();
      return Number.isFinite(res) && res > 0 ? res : 0;
    } catch {
      return 0;
    }
  }, [amountRaw]);

  const quickAmounts = [100, 200, 500, 1000];

  const handleQuickAdd = (val) => {
    setAmountRaw(String(amtNum + val));
    triggerHaptic(hapticImpact.light);
  };

  const [catUsage, setCatUsage] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('category_usage')) || {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`/api/categories?kind=${type}`);
        const data = await res.json();
        setCats(Array.isArray(data) ? data : []);
      } catch {
        setCats([]);
      }
    })();
  }, [type]);

  const sortedCats = useMemo(() => {
    return [...cats].sort((a, b) => {
      const countA = catUsage[a.id] || 0;
      const countB = catUsage[b.id] || 0;
      if (countB !== countA) {
        return countB - countA;
      }
      return a.name.localeCompare(b.name);
    });
  }, [cats, catUsage]);

  const renderDescriptionWithHighlights = (text) => {
    if (!text) return '';
    const parts = text.split(/(#[а-яА-ЯёЁa-zA-Z0-9_]+)/g);
    return parts.map((part, i) => {
      if (part.startsWith('#')) {
        return (
          <span key={i} className={styles.hashtagHighlight}>
            {part}
          </span>
        );
      }
      return part;
    });
  };

  const handleDescScroll = (e) => {
    if (overlayRef.current) {
      overlayRef.current.scrollTop = e.target.scrollTop;
      overlayRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  function switchType(nextType) {
    if (nextType === type) return;
    setType(nextType);
    triggerHaptic(hapticImpact.medium);

    if (isRememberEnabled) {
      const savedCategory = localStorage.getItem(`last_${nextType}_category`);
      setCategoryId(savedCategory || '');
    } else {
      setCategoryId('');
    }

    setCategoryError('');
  }

  const handleCategorySelect = (id) => {
    setCategoryId(id);
    triggerHaptic(hapticImpact.light);

    if (touchedSubmit) {
      setCategoryError(String(id || '').trim() ? '' : 'Выберите категорию');
    }

    const picked = cats.find((c) => String(c.id) === String(id));
    if (picked?.kind === 'income') switchType('income');
    if (picked?.kind === 'expense') switchType('expense');
  };

  const isAmountOk = Number.isFinite(amtNum) && amtNum > 0;
  const isCategoryOk = !!String(categoryId || '').trim();

  const isFormReady = isAmountOk && isCategoryOk;
  const canSubmit = submitState === 'idle' && isFormReady;

  useEffect(() => {
    if (!touchedSubmit) return;
    setAmountError(isAmountOk ? '' : 'Сумма должна быть больше 0');
    setCategoryError(isCategoryOk ? '' : 'Выберите категорию');
  }, [touchedSubmit, isAmountOk, isCategoryOk]);

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  async function handleSubmit(e) {
    e.preventDefault();
    setTouchedSubmit(true);

    const nextAmountError = isAmountOk ? '' : 'Сумма должна быть больше 0';
    const nextCategoryError = isCategoryOk ? '' : 'Выберите категорию';

    setAmountError(nextAmountError);
    setCategoryError(nextCategoryError);

    if (nextAmountError || nextCategoryError) {
      triggerHaptic(hapticImpact.error);
      return;
    }

    try {
      setSubmitState('saving');

      const payload = {
        type,
        amount: Math.round(amtNum),
        category_id: Number(categoryId),
        date,
        time,
        description: description.trim(),
      };

      const res = await fetch('/api/transactions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let msg = 'Ошибка сохранения';
        try {
          const data = await res.json();
          if (data?.error) msg = data.error;
        } catch {}
        throw new Error(msg);
      }

      const created = await res.json();
      onCreated?.(created);

      const newUsage = {
        ...catUsage,
        [categoryId]: (catUsage[categoryId] || 0) + 1,
      };
      setCatUsage(newUsage);
      localStorage.setItem('category_usage', JSON.stringify(newUsage));

      if (localStorage.getItem('remember_last_category') === 'true') {
        localStorage.setItem(`last_${type}_category`, String(categoryId));
      }

      setAmountRaw('');
      setDescription('');

      if (!isRememberEnabled) {
        setCategoryId('');
        setType('expense');
      }

      setTouchedSubmit(false);
      setAmountError('');
      setCategoryError('');

      await sleep(600);
      
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      setSubmitState('success');
      
      triggerHaptic(hapticImpact.success);

      setTimeout(() => {
        setSubmitState('idle');
        setIsModalOpen(false);
        
        if (!isMobile) {
          requestAnimationFrame(() => amountRef.current?.focus?.());
        }
      }, 1000);
    } catch {
      setSubmitState('idle');
      setAmountError('');
      setCategoryError('');
      setTouchedSubmit(true);
      triggerHaptic(hapticImpact.error); 
    }
  }

  const amountMeasureRef = useRef(null);
  const [amountWidth, setAmountWidth] = useState(0);

  useEffect(() => {
    requestAnimationFrame(() => {
      if (!amountMeasureRef.current) return;
      const w = amountMeasureRef.current.offsetWidth;
      setAmountWidth(Math.min(w, 250));
    });
  }, [amountView]);

  const handleChipsWheel = (e) => {
    if (e.deltaY !== 0) {
      e.currentTarget.scrollLeft += e.deltaY;
    }
  };

  const evaluateAmount = () => {
    if (amtNum > 0) {
      let res = String(Math.round(amtNum));
      if (res.length > 9) res = res.slice(0, 9);
      setAmountRaw(res);
      triggerHaptic(hapticImpact.light);
    }
  };

  const formContent = (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.form__inner}>
        <div
          className={[
            styles.form__tabs,
            type === 'income'
              ? styles.form__tabs_income
              : styles.form__tabs_expense,
          ].join(' ')}
          role='tablist'
          aria-label='Тип операции'>
          <div className={styles.form__tabIndicator} aria-hidden='true' />

          <button
            type='button'
            role='tab'
            aria-selected={type === 'expense'}
            className={styles.form__tab}
            onClick={() => switchType('expense')}>
            Расход
          </button>

          <button
            type='button'
            role='tab'
            aria-selected={type === 'income'}
            className={styles.form__tab}
            onClick={() => switchType('income')}>
            Доход
          </button>
        </div>

        <label className={styles.form__label}>
          <div className={styles.value__block}>
            <span ref={amountMeasureRef} className={styles.value__measure}>
              {amountView || '0'}
            </span>

            {amountRaw && (
              <span
                className={[
                  styles.value__sign,
                  type === 'income'
                    ? styles.value__sign_income
                    : styles.value__sign_expense,
                ].join(' ')}
                style={{ '--half': `${amountWidth / 2}px` }}>
                {type === 'income' ? '+' : '−'}
              </span>
            )}

            <input
              ref={amountRef}
              type='text'
              inputMode='decimal'
              className={[
                styles.form__value,
                type === 'income'
                  ? styles.value__input_income
                  : styles.value__input_expense,
                amountError ? styles.value__inputError : '',
              ].join(' ')}
              value={amountView}
              onChange={(e) => {
                let v = e.target.value.replace(/[^\d+\-*/]/g, '');

                if (!/[+\-*/]/.test(v) && v.length > 9) {
                  v = v.slice(0, 9);
                } else if (v.length > 14) {
                  v = v.slice(0, 14);
                }

                setAmountRaw(v);
              }}
              onBlur={evaluateAmount}
              onKeyDown={(e) => {
                if (e.key === 'Backspace') {
                  const el = e.target;
                  const cursor = el.selectionStart;
                  if (cursor > 0 && el.value[cursor - 1] === ' ') {
                    el.setSelectionRange(cursor - 1, cursor - 1);
                  }
                }
                if (e.key === '=') {
                  e.preventDefault();
                  evaluateAmount();
                  return;
                }
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  evaluateAmount();
                  requestAnimationFrame(() => descRef.current?.focus?.());
                }
              }}
            />

            {!amountRaw && (
              <span className={styles.value__placeholder}>2500 ₽</span>
            )}

            {amountRaw && (
              <span
                className={[
                  styles.value__currency,
                  type === 'income'
                    ? styles.value__currency_income
                    : styles.value__currency_expense,
                ].join(' ')}
                style={{ '--half': `${amountWidth / 2}px` }}>
                ₽
              </span>
            )}
          </div>

          <div className={styles.quickAmounts}>
            {quickAmounts.map((val) => (
              <button
                key={val}
                type='button'
                className={styles.quickAmountBtn}
                onClick={() => handleQuickAdd(val)}>
                +{val}
              </button>
            ))}
          </div>

          {amountError ? (
            <div className={styles.form__fieldError}>{amountError}</div>
          ) : null}
        </label>

        <div className={styles.chipsWrapper}>
          <div className={styles.chipsContainer} onWheel={handleChipsWheel}>
            {sortedCats.map((c) => {
              const isActive = String(categoryId) === String(c.id);

              const Icon =
                categoryIcons?.[c.icon] || categoryIcons?.Star || null;

              return (
                <button
                  key={c.id}
                  type='button'
                  className={[
                    styles.chip,
                    isActive ? styles.chip_active : '',
                  ].join(' ')}
                  onClick={() => handleCategorySelect(c.id)}>
                  {Icon && (
                    <span className={styles.chip__icon}>
                      <Icon />
                    </span>
                  )}
                  <span className={styles.chip__text}>{c.name}</span>
                </button>
              );
            })}
          </div>
          {categoryError ? (
            <div className={styles.form__fieldError}>{categoryError}</div>
          ) : null}
        </div>

        <label className={styles.form__label}>
          <WheelDateTimePicker
            date={date}
            time={time}
            daysBack={180}
            onDateChange={setDate}
            onTimeChange={setTime}
          />
        </label>

        <label className={styles.form__label}>
          <div className={styles.descWrapper}>
            <div ref={overlayRef} className={styles.descOverlay}>
              {renderDescriptionWithHighlights(description)}
              {description.endsWith('\n') || !description ? '\u00A0' : ''}
            </div>
            <textarea
              ref={descRef}
              className={styles.form__description}
              placeholder='Комментарий к операции'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onScroll={handleDescScroll}
              rows={1}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (submitState === 'idle')
                    e.currentTarget.form?.requestSubmit?.();
                }
              }}
            />
          </div>
        </label>

        <button
          type='submit'
          className={[
            styles.form__submit,
            isFormReady ? styles.form__submitReady : '',
            submitState === 'success' ? styles.form__submitSuccess : '',
          ].join(' ')}
          disabled={!canSubmit}>
          {submitState === 'saving'
            ? 'Сохранение...'
            : submitState === 'success'
            ? 'Добавлено'
            : 'Добавить'}
        </button>
      </div>
    </form>
  );

  if (isMobile) {
    return (
      <>
        <button 
          className={styles.fab} 
          onClick={() => {
            triggerHaptic(hapticImpact.heavy);
            setIsModalOpen(true);
          }}>
          +
        </button>

        <UniversalModal
          isOpen={isModalOpen}
          onClose={() => {
            triggerHaptic(hapticImpact.light);
            setIsModalOpen(false);
          }}>
          {formContent}
        </UniversalModal>

        <AnimatePresence>
          {submitState === 'success' && (
            <SuccessOverlay isVisible={true} />
          )}
        </AnimatePresence>
      </>
    );
  }

  return (
    <>
      {formContent}
      <AnimatePresence>
        {submitState === 'success' && (
          <SuccessOverlay isVisible={true} />
        )}
      </AnimatePresence>
    </>
  );
}